import { ActionType } from 'pages/OrderLifeCycle/types/ActionType';
import { OrderDetailsType } from 'pages/OrderLifeCycle/types/OrderDetailsType';
import { PaymentMethod } from 'models/PaymentMethod';

export type P2pDepositCompletedEvent = {
	event: 'p2p_deposit_completed';
	order_id: string;
	value: number;
	currency: 'USD';
	local_amount: number;
	local_currency: string;
	payment_method: string;
	merchant_name: string;
};

const GTM_TRIGGER_ACTIONS: ActionType[] = ['CONFIRM_BUYER_TRANSFERRED'];

const parseNumericAmount = (value: string | number | undefined): number => {
	if (value === undefined || value === null || value === '') return 0;
	if (typeof value === 'number') return value;
	return parseFloat(String(value).replace(/,/g, '')) || 0;
};

export const parseAmountWithCurrency = (value: string | number | undefined): { amount: number; currency: string } => {
	if (value === undefined || value === null || value === '') {
		return { amount: 0, currency: '' };
	}
	if (typeof value === 'number') {
		return { amount: value, currency: '' };
	}

	const trimmed = String(value).trim();
	const match = trimmed.match(/^([\d.,]+)\s+([A-Za-z]{2,10})$/);

	if (match) {
		return {
			amount: parseFloat(match[1].replace(/,/g, '')) || 0,
			currency: match[2].trim(),
		};
	}

	return { amount: parseNumericAmount(trimmed), currency: '' };
};

export const shouldTriggerP2pDepositGtmOnActionSuccess = (
	order: OrderDetailsType,
	isMerchant: boolean,
	action: ActionType,
	newStatus?: string,
): boolean => {
	if (order.type !== 'BUY' || isMerchant) return false;
	if (!GTM_TRIGGER_ACTIONS.includes(action)) return false;
	if (!order.actions.includes(action)) return false;
	return true;
};

export const buildP2pDepositCompletedEvent = (
	order: OrderDetailsType,
	paymentMethods: PaymentMethod[],
): P2pDepositCompletedEvent => {
	const paymentMethod = paymentMethods.find((method) => method.methodId === order.paymentMethod);
	const { amount: localAmount, currency: localCurrency } = parseAmountWithCurrency(order.amount);

	return {
		event: 'p2p_deposit_completed',
		order_id: String(order.orderNumber),
		value: parseNumericAmount(order.quantity),
		currency: 'USD',
		local_amount: localAmount,
		local_currency: localCurrency,
		payment_method: paymentMethod?.methodName ?? '',
		merchant_name: order.merchant?.fullName ?? '',
	};
};

export const pushP2pDepositCompletedEventOnActionSuccess = (
	order: OrderDetailsType,
	isMerchant: boolean,
	action: ActionType,
	paymentMethods: PaymentMethod[],
	newStatus?: string,
): boolean => {
	if (!shouldTriggerP2pDepositGtmOnActionSuccess(order, isMerchant, action, newStatus)) return false;

	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push(buildP2pDepositCompletedEvent(order, paymentMethods));

	return true;
};
